// frontend/src/components/handbook/HandbookSidebar.js
import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { getChapterProgress } from "../../utils/handbookProgress";
import ContentTypeBadge from "./ContentTypeBadge";
import ChapterStatusBadge, { titleStyleForStatus } from "./ChapterStatusBadge";

const HandbookSidebar = ({ handbook }) => {
  const { chapterSlug } = useParams();
  const [openSections, setOpenSections] = useState({});

  // Open only the section that contains the active chapter; on overview, open all
  useEffect(() => {
    if (!handbook?.sections) return;
    const next = {};
    for (const section of handbook.sections) {
      const containsActive = section.chapters.some(
        (c) => c.slug === chapterSlug
      );
      next[section.id] = chapterSlug ? containsActive : true;
    }
    setOpenSections(next);
  }, [chapterSlug, handbook]);

  if (!handbook?.sections) return null;

  const toggleSection = (id) => {
    setOpenSections((s) => ({ ...s, [id]: !s[id] }));
  };

  const sectionAggregate = (section) => {
    let done = 0;
    let total = 0;
    for (const c of section.chapters) {
      const { done: d, total: t } = getChapterProgress(c.slug, c.checklists);
      done += d;
      total += t;
    }
    return { done, total };
  };

  return (
    <aside className="hidden md:block w-72 lg:w-80 flex-shrink-0">
      <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-3">
        <nav>
          {handbook.sections.map((section) => {
            const isOpen = !!openSections[section.id];
            const { done, total } = sectionAggregate(section);
            return (
              <div key={section.id} className="mb-2">
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded hover:bg-gray-50 group"
                >
                  <div className="flex items-center gap-1.5 text-left min-w-0">
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-xs uppercase tracking-wider text-[#002855] font-semibold truncate">
                      {section.title}
                    </span>
                  </div>
                  {total > 0 && (
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {done}/{total}
                    </span>
                  )}
                </button>

                {isOpen && (
                  <ul className="mt-0.5 ml-2 border-l border-gray-100">
                    {section.chapters.map((chapter) => {
                      const isActive = chapter.slug === chapterSlug;
                      const { done: cd, total: ct } = getChapterProgress(
                        chapter.slug,
                        chapter.checklists
                      );
                      const allDone = ct > 0 && cd === ct;
                      return (
                        <li key={chapter.slug}>
                          <Link
                            to={`/coach-handbook/${chapter.slug}`}
                            className={`flex items-center gap-2 pl-2 pr-2 py-1.5 -ml-px border-l-2 text-sm transition-colors ${
                              isActive
                                ? "border-[#84BD00] text-[#002855] font-medium bg-[#84BD00]/5"
                                : "border-transparent text-gray-600 hover:text-[#002855] hover:border-gray-300"
                            }`}
                          >
                            <ContentTypeBadge
                              type={chapter.content_type}
                              compact
                              className="flex-shrink-0"
                            />
                            <span
                              className={`truncate flex-1 ${titleStyleForStatus(
                                chapter.status
                              )}`}
                            >
                              {chapter.title}
                            </span>
                            <ChapterStatusBadge
                              status={chapter.status}
                              compact
                              className="flex-shrink-0"
                            />
                            {ct > 0 && (
                              <span
                                className={`text-xs flex-shrink-0 flex items-center ${
                                  allDone ? "text-[#84BD00]" : "text-gray-400"
                                }`}
                              >
                                {allDone ? (
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                ) : (
                                  `${cd}/${ct}`
                                )}
                              </span>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default HandbookSidebar;
